/* Created by Interactive 63 - https://interactive63.com */
/**
 * Reading and rewriting shapes in the stored SVG source: finding an element's path data,
 * converting a basic shape into a path without changing how it looks, and replacing a path's data.
 */
import type { ElementId } from '../model/types';
import { SHAPE_ATTRIBUTES, shapeToPathData, type ShapeAttributes } from './shapes';

const SVG_NS = 'http://www.w3.org/2000/svg';

/** Finds an element by its stable ID in a parsed source. */
function find(doc: Document, id: ElementId): Element | null {
  return doc.querySelector(`[data-st-id="${id}"]`);
}

/** Reads an element's attributes into a plain object. */
function attributesOf(el: Element): ShapeAttributes {
  const attrs: ShapeAttributes = {};
  for (const attr of Array.from(el.attributes)) attrs[attr.name] = attr.value;
  return attrs;
}

/** Path data for a path or basic shape element, or an empty string for anything else. */
export function geometryOf(el: Element): string {
  const tag = el.localName;
  return tag === 'path' ? (el.getAttribute('d') ?? '') : shapeToPathData(tag, attributesOf(el));
}

/** The path data of an element in the stored source, whatever its shape type. */
export function originalPathData(source: string, id: ElementId): string {
  const el = find(new DOMParser().parseFromString(source, 'image/svg+xml'), id);
  return el ? geometryOf(el) : '';
}

/**
 * Replaces a basic shape in the source with a `<path>` that draws the same outline. Presentation
 * attributes, id, class, transform, style and the stable ID are kept; the geometry attributes
 * are dropped. Returns null if the element is missing or not a convertible shape.
 */
export function convertShapeInSource(
  source: string,
  id: ElementId,
): { source: string; d: string } | null {
  const doc = new DOMParser().parseFromString(source, 'image/svg+xml');
  const el = find(doc, id);
  if (!el || el.localName === 'path') return null;
  const d = shapeToPathData(el.localName, attributesOf(el));
  if (d === '') return null;
  const path = doc.createElementNS(SVG_NS, 'path');
  for (const attr of Array.from(el.attributes)) {
    if (!SHAPE_ATTRIBUTES.includes(attr.name)) path.setAttribute(attr.name, attr.value);
  }
  path.setAttribute('d', d);
  el.replaceWith(path);
  return { source: new XMLSerializer().serializeToString(doc.documentElement), d };
}

/** Replaces a path's data in the source. Returns null when the element is missing or not a path. */
export function setPathDataInSource(source: string, id: ElementId, d: string): string | null {
  const doc = new DOMParser().parseFromString(source, 'image/svg+xml');
  const el = find(doc, id);
  if (el?.localName !== 'path') return null;
  el.setAttribute('d', d);
  return new XMLSerializer().serializeToString(doc.documentElement);
}
